import React, { useState, useEffect, useRef } from 'react';
import { X, CheckCircle, Banknote, Info, Ticket, FileText, AlertTriangle, Search } from 'lucide-react';
import { createPortal } from 'react-dom';
import { supabase } from '../../src/lib/supabase';
import { buscarPagadorDaUc } from '../../src/services/pagadores';
import { podeEditarCadastro } from '../../src/lib/permissaoCadastro';
import { atualizarUcComMotivo } from '../../src/services/cadastroUc';
import { emitirCobrancaAvulsa } from '../../src/lib/emitirCobrancaAvulsa';
import { logAction, logUsageEvent } from '../../src/lib/logger';
import { useToast } from '../../src/contexts/ToastContext';
import { useAuth } from '../../src/contexts/AuthContext';
import ConfirmationModal from '../../src/components/ConfirmationModal';
import { addDaysISO } from '../../src/lib/janelaCobranca';
import { ressalvaValida, gravarRessalvaPagador } from '../../src/lib/ressalvaPagador';
import ConflitoPagadorAviso from './components/ConflitoPagadorAviso';
import { alertaPagadorFilialDivergente, situacaoDocumento, conflitoDePagador, type ConflitoPagador } from '../../src/lib/pagadorBoleto';
import { normalizeUcCodigo } from '../../src/lib/ucCodigo';
import { normalizeMesReferencia, isMesReferenciaCanonico } from '../../src/lib/mesReferencia';
import { propsFundoModal } from '../../src/lib/backdropModal';

interface BoletoAvulsoModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: () => void;
}

const BoletoAvulsoModal: React.FC<BoletoAvulsoModalProps> = ({ isOpen, onClose, onSuccess }) => {
    const { addToast } = useToast();
    const { user, profile } = useAuth();
    const [loading, setLoading] = useState(false);
    const [searchingUC, setSearchingUC] = useState(false);
    const [showEmitConfirm, setShowEmitConfirm] = useState(false);
    const [emissionError, setEmissionError] = useState<string | null>(null);
    const [alertaPagador, setAlertaPagador] = useState<string | null>(null);
    const [ucId, setUcId] = useState<string | null>(null);
    const [ucRegistered, setUcRegistered] = useState<boolean>(true);
    const [shouldSaveUc, setShouldSaveUc] = useState<boolean>(false);
    const [showUcConfirmModal, setShowUcConfirmModal] = useState<boolean>(false);
    // Armado pelo guard de retentativa: o segundo clique em Emitir passa direto,
    // para não travar quem realmente quer duas cobranças iguais (parcelamento).
    const [confirmouDuplicata, setConfirmouDuplicata] = useState<boolean>(false);
    // Documento já usado com outro nome — vira confirmação. Ref porque `handleEmitir`
    // é rechamado logo após confirmar e leria o state antigo.
    const [pendingConflito, setPendingConflito] = useState<ConflitoPagador | null>(null);
    const conflitoLiberadoRef = useRef(false);
    // Pessoas diferentes no mesmo CPF/CNPJ: qualquer um emite, com ressalva escrita (#6160).
    const [ressalvaPagador, setRessalvaPagador] = useState('');
    const ressalvaAceitaRef = useRef<string | null>(null);
    const faltaRessalva = !!pendingConflito?.pessoaDiferente && !ressalvaValida(ressalvaPagador);
    // Controla se o usuário já editou manualmente a data limite de pagamento.
    // Enquanto false, a data limite acompanha o vencimento (vencimento + 30).
    const [limiteEditadoManualmente, setLimiteEditadoManualmente] = useState<boolean>(false);
    // Marca a cobrança como saldo remanescente (faturas.saldo_remanescente), que o card
    // "Saldo Remanescente" da tela de Faturas filtra. Sem ela o motivo ia escrito no
    // campo do mês e quebrava o filtro de mês.
    const [saldoRemanescente, setSaldoRemanescente] = useState(false);

    const [edited, setEdited] = useState({
        uc_codigo: '',
        nome_cliente_boleto: '',
        cpf_cnpj_cliente_boleto: '',
        end_logradouro: '',
        end_numero: '',
        end_bairro: '',
        end_cep: '',
        end_cidade: '',
        end_uf: '',
        mes_referencia: '',
        valor_boleto_vigor: 0,
        vencimento_boleto: '',
        data_limite_pagamento_boleto: '',
        valor_consumo_nao_comp: 0,
        valor_energia_nao_isenta_boleto: 0,
        valor_restituicao_imposto_boleto: 0,
        outros_multas_boleto: 0,
        outros_multas_descricao: '',
        energia_injetada_gd1_kwh: 0,
        tarifa_injecao_gd1: 0,
        consumo_ativo_kwh: 0,
        tarifa_bandeira: 0,
        energia_injetada_gd2_kwh: 0,
        tarifa_injecao_gd2: 0,
        valor_parcelamentos_boleto: 0,
        valor_juros_boleto: 0,
        // Novos campos para sincronia com BoletoEmissionModal
        tarifa_parc_injet_s_desc_gd2: 0,
        qtd_injecao_scee_boleto: 0,
        qtd_adc_bandeira_boleto: 0,
        qtd_parc_injet_boleto: 0,
        parcelamento_descricao: ''
    });

    // Auto-search UC info
    const searchUC = async (code: string) => {
        if (!code || code.length < 3) return;
        setSearchingUC(true);
        try {
            // Mesma correção do BoletoEmissionModal: a resolução é do banco, que casa
            // exato OU normalizado (UC cadastrada como '2.460.767.012-08' vs código
            // digitado só com dígitos) e desempata as UCs duplicadas. Com `.eq` puro,
            // toda UC cadastrada formatada caía no "UC Não Encontrada".
            const { data: ucList, error } = await supabase
                .rpc('resolver_uc_por_codigo', { p_codigo: code });

            const data = (Array.isArray(ucList) ? ucList[0] : ucList) || null;

            if (data) {
                setUcId(data.id);
                setUcRegistered(true);
                // Pagador da UC (tabela pagadores) manda: nome e documento juntos,
                // da mesma fonte. Sem ele, o cadastro da UC, como antes.
                const pagadorUc = await buscarPagadorDaUc(data.id);
                setEdited(prev => ({
                    ...prev,
                    nome_cliente_boleto: pagadorUc ? pagadorUc.nome : (data.nome_cliente || ''),
                    cpf_cnpj_cliente_boleto: pagadorUc ? (pagadorUc.cpf_cnpj || '') : (data.cpf_cnpj_cliente || data.cpf_cnpj_titular || ''),
                    end_logradouro: data.logradouro || '',
                    end_numero: data.numero || '',
                    end_bairro: data.bairro || '',
                    end_cep: data.cep || '',
                    end_cidade: data.cidade || '',
                    end_uf: data.estado || '',
                }));
                // Pagador de outra filial da mesma empresa que o titular quase sempre é
                // cadastro trocado — foi o que fez sair boleto de Marzagão no CNPJ da
                // Água Limpa. Avisa, mas não impede: a decisão é de quem emite.
                setAlertaPagador(alertaPagadorFilialDivergente(
                    data.cpf_cnpj_cliente || data.cpf_cnpj_titular,
                    data.cpf_cnpj_titular,
                ));
                addToast('Dados da Unidade Consumidora carregados!', 'success');
            } else {
                setUcId(null);
                setUcRegistered(false);
            }
        } catch (error) {
            console.error('Error searching UC:', error);
        } finally {
            setSearchingUC(false);
        }
    };

    // Auto-recalculate valor_boleto_vigor when detailed fields change (Standardized logic)
    // Liberação de conflito de pagador vale para uma emissão só.
    useEffect(() => {
        if (!isOpen) return;
        conflitoLiberadoRef.current = false;
        ressalvaAceitaRef.current = null;
        setRessalvaPagador('');
        setPendingConflito(null);
        setSaldoRemanescente(false);
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;

        // Para Avulso, não temos o contexto de desconto de bandeira de uma fatura anterior, logo usamos 0.
        const row1 = edited.qtd_injecao_scee_boleto * edited.tarifa_injecao_gd1;
        const row2 = edited.qtd_adc_bandeira_boleto * edited.tarifa_bandeira; // Sem desconto para avulso por padrão
        const row3 = -(edited.qtd_parc_injet_boleto * edited.tarifa_parc_injet_s_desc_gd2);
        const row4 = edited.valor_restituicao_imposto_boleto;
        const row5 = edited.valor_energia_nao_isenta_boleto;
        const row6 = edited.valor_juros_boleto + edited.outros_multas_boleto;
        const row7 = edited.valor_parcelamentos_boleto;

        const dynamicTotal = row1 + row2 + row3 + row4 + row5 + row6 + row7;

        setEdited(prev => {
            if (Math.abs(prev.valor_boleto_vigor - dynamicTotal) > 0.001) {
                return { ...prev, valor_boleto_vigor: dynamicTotal };
            }
            return prev;
        });

    }, [
        edited.qtd_injecao_scee_boleto,
        edited.qtd_adc_bandeira_boleto,
        edited.qtd_parc_injet_boleto,
        edited.tarifa_injecao_gd1,
        edited.tarifa_bandeira,
        edited.tarifa_parc_injet_s_desc_gd2,
        edited.valor_restituicao_imposto_boleto,
        edited.valor_energia_nao_isenta_boleto,
        edited.valor_juros_boleto,
        edited.outros_multas_boleto,
        edited.valor_parcelamentos_boleto,
        isOpen
    ]);

    const handleEmitir = async () => {
        // Validation: All Client Data is mandatory
        const requiredFields = [
            'uc_codigo', 'nome_cliente_boleto', 'cpf_cnpj_cliente_boleto',
            'end_logradouro', 'end_numero', 'end_bairro', 'end_cep',
            'end_cidade', 'end_uf', 'mes_referencia', 'vencimento_boleto'
        ];

        for (const field of requiredFields) {
            if (!edited[field as keyof typeof edited]) {
                addToast(`O campo ${field} é obrigatório nos Dados do Cliente.`, 'error');
                return;
            }
        }

        // Era só aviso e passava: "SET/2026 - SALDO REMANESCENTE - UC SEM CONSUMO" virou
        // o ano "2026-SALDOREMANESCENTE..." no filtro de mês da tela de Faturas.
        if (!isMesReferenciaCanonico(edited.mes_referencia)) {
            addToast('Mês Ref. aceita só o mês, no formato MM/AAAA. O motivo da cobrança vai na descrição.', 'error');
            return;
        }

        if (edited.valor_boleto_vigor <= 0) {
            addToast('O valor do boleto deve ser maior que zero.', 'error');
            return;
        }

        if (
            edited.data_limite_pagamento_boleto &&
            edited.data_limite_pagamento_boleto < edited.vencimento_boleto
        ) {
            addToast('A data limite de pagamento não pode ser anterior ao vencimento do boleto.', 'error');
            return;
        }

        // O Sicoob mantém UM cadastro de pagador por CPF/CNPJ: documento errado (ou de
        // outro cliente) reescreve o nome do pagador de todos os boletos daquele
        // documento — foi o que aconteceu com AFCOM x ARE. Ver src/lib/pagadorBoleto.ts.
        const situacaoDoc = situacaoDocumento(edited.cpf_cnpj_cliente_boleto);
        if (situacaoDoc === 'invalido') {
            addToast(`CPF/CNPJ do pagador é inválido: ${edited.cpf_cnpj_cliente_boleto}. Corrija antes de emitir.`, 'error');
            return;
        }
        // Conflito é confirmação, não bloqueio: o histórico pode estar sujo justamente
        // pelo erro que se quer corrigir, e barrar de vez deixa o operador sem saída.
        if (!conflitoLiberadoRef.current) {
            const conflito = await conflitoDePagador(edited.cpf_cnpj_cliente_boleto, edited.nome_cliente_boleto);
            if (conflito) {
                setPendingConflito(conflito);
                return;
            }
        }

        setLoading(true);
        setEmissionError(null);

        try {
            // Guard: bloqueia se já existe fatura NORMAL ativa para o mesmo UC + mês.
            // Cobranças AVULSO nunca são consideradas duplicata (mesmo critério do trigger
            // prevent_duplicate_active_fatura no banco), pois é comum emitir mais de uma
            // avulsa no mesmo mês/UC (ex: parcelamento de um acordo em várias cobranças).
            //
            // Casa por uc_codigo_norm (coluna gerada, migration 20260803130000): o robô
            // grava "422101001274" e a avulsa manual "4.221.010.012-74". Comparando a
            // string crua, a mesma UC nunca batia e a trava passava batido.
            const ucNorm = normalizeUcCodigo(edited.uc_codigo);
            const mesNorm = normalizeMesReferencia(edited.mes_referencia);

            const { data: faturaExistente } = await supabase
                .from('faturas')
                .select('id, status_fatura')
                .eq('uc_codigo_norm', ucNorm)
                .eq('mes_referencia', mesNorm)
                .or('classificacao.is.null,classificacao.neq.AVULSO')
                .not('status_fatura', 'in', '("Excluído","Cancelada","Aguardando Upload")')
                .limit(1);

            if (faturaExistente && faturaExistente.length > 0) {
                setEmissionError(
                    `Já existe uma fatura ativa para a UC ${edited.uc_codigo} em ${mesNorm} (status: ${faturaExistente[0].status_fatura}). Exclua ou cancele a fatura existente antes de criar uma nova.`
                );
                setLoading(false);
                return;
            }

            // Guard: retentativa. Avulsa duplicada é liberada de propósito (acima), mas
            // mesma UC + mesmo valor + mesmo vencimento criada há poucos minutos é quase
            // sempre o operador reemitindo porque a cobrança "não apareceu" na tela — e
            // cada clique deixava uma linha órfã "Em aberto" inflando Vencido/A Receber.
            const JANELA_RETENTATIVA_MIN = 30;
            const desde = new Date(Date.now() - JANELA_RETENTATIVA_MIN * 60_000).toISOString();
            const { data: recenteIgual } = await supabase
                .from('faturas')
                .select('id, mes_referencia, created_at, num_cobranca_boleto')
                .eq('uc_codigo_norm', ucNorm)
                .eq('valor_boleto_vigor', edited.valor_boleto_vigor)
                .eq('vencimento_boleto', edited.vencimento_boleto)
                .not('status_fatura', 'in', '("Excluído","Cancelada","Aguardando Upload")')
                .gte('created_at', desde)
                .limit(1);

            if (recenteIgual && recenteIgual.length > 0 && !confirmouDuplicata) {
                const j = recenteIgual[0];
                setEmissionError(
                    `Já existe uma cobrança igual criada há poucos minutos para esta UC — R$ ${edited.valor_boleto_vigor} vencendo em ${edited.vencimento_boleto} (fatura ${j.id}, mês "${j.mes_referencia}"${j.num_cobranca_boleto ? `, boleto ${j.num_cobranca_boleto}` : ', sem boleto gerado'}). ` +
                    `Se ela não aparece na tela, troque o filtro de mês para "Todos" antes de emitir de novo. Clique em Emitir outra vez para criar assim mesmo.`
                );
                setConfirmouDuplicata(true);
                setLoading(false);
                return;
            }

            // 1. Check if UC exists, if not create it.
            // Busca pelas colunas normalizadas (migration 20260811151000): comparar a
            // string crua fazia "4.221.010.012-74" não achar o cadastro "422101001274"
            // e criar uma UC nova — origem dos cadastros duplicados.
            const { data: ucList } = await supabase
                .from('unidades_consumidoras')
                .select('id, numero_uc, outro_numero_uc, numero_uc_norm, outro_numero_uc_norm')
                .or(`numero_uc_norm.eq.${ucNorm},outro_numero_uc_norm.eq.${ucNorm}`);

            const findBestUC = (list: any[], codeNorm: string) => {
                if (!list || list.length === 0) return null;
                const primaryMatch = list.find(uc => uc.numero_uc_norm === codeNorm);
                if (primaryMatch) return primaryMatch;
                const secondaryMatch = list.find(uc => uc.outro_numero_uc_norm === codeNorm);
                if (secondaryMatch) return secondaryMatch;
                return list[0];
            };

            const existingUC = findBestUC(ucList || [], ucNorm);
            setUcRegistered(!!existingUC);

            if (!existingUC) {
                setShowUcConfirmModal(true);
                setLoading(false);
                return;
            }

            executeEmitir(existingUC);
        } catch (err) {
            console.error(err);
            addToast('Erro ao validar UC.', 'error');
            setLoading(false);
        }
    };

    const executeEmitir = async (existingUC: any | null = null) => {
        setLoading(true);
        setEmissionError(null);

        // Recalculado aqui (função pura) porque handleEmitir é outro escopo — e é este
        // valor que vai para o banco, não o texto digitado.
        const mesNorm = normalizeMesReferencia(edited.mes_referencia);

        try {
            if (existingUC && !podeEditarCadastro(profile)) {
                // #6239: sem a permissão "Editar dados cadastrais", o boleto avulso não
                // grava nome/CPF/endereço na UC (o banco barraria).
            } else if (existingUC) {
                // O gatilho só registra o que mudou de fato; o motivo diz de onde veio.
                const { error: ucError } = await atualizarUcComMotivo(existingUC.id, {
                        nome_cliente: edited.nome_cliente_boleto,
                        cpf_cnpj_cliente: edited.cpf_cnpj_cliente_boleto,
                        logradouro: edited.end_logradouro,
                        numero: edited.end_numero,
                        bairro: edited.end_bairro,
                        cep: edited.end_cep,
                        cidade: edited.end_cidade,
                        estado: edited.end_uf,
                        endereco: `${edited.end_logradouro}, ${edited.end_numero} - ${edited.end_bairro}, ${edited.end_cidade}/${edited.end_uf}`
                    }, `Corrigido na emissão de boleto avulso (${mesNorm || 'sem mês de referência'}).`);
                if (ucError) console.error("Erro ao sincronizar Unidade Consumidora:", ucError);
            } else if (shouldSaveUc) {
                const { error: ucError } = await supabase.from('unidades_consumidoras').insert({
                    numero_uc: edited.uc_codigo.trim(),
                    nome_cliente: edited.nome_cliente_boleto,
                    cpf_cnpj_cliente: edited.cpf_cnpj_cliente_boleto,
                    logradouro: edited.end_logradouro,
                    numero: edited.end_numero,
                    bairro: edited.end_bairro,
                    cep: edited.end_cep,
                    cidade: edited.end_cidade,
                    estado: edited.end_uf,
                    endereco: `${edited.end_logradouro}, ${edited.end_numero} - ${edited.end_bairro}, ${edited.end_cidade}/${edited.end_uf}`,
                    // Situação fica indefinida até o rateio confirmar a UC — cadastro
                    // avulso não implica que ela está de fato recebendo energia.
                    situacao: null,
                    // Usuário de empresa parceira só cadastra UC para a própria empresa
                    // (RLS já reforça isso).
                    ...(profile?.role === 'empresa_parceira_admin' || profile?.role === 'empresa_parceira_operador'
                        ? { empresa_parceira_id: profile.empresa_parceira_id }
                        : {}),
                });
                if (ucError) throw new Error("Erro ao criar Unidade Consumidora: " + ucError.message);
            }

            // 2. Cria a fatura e emite. A sequência inteira (insert -> conta Sicoob ->
            // webhook -> prova de que a cobrança existe -> status) mora em
            // src/lib/emitirCobrancaAvulsa.ts, compartilhada com a cobrança
            // unificada da Central de Inadimplência.
            const totalComVigor = edited.valor_boleto_vigor - edited.valor_juros_boleto - edited.outros_multas_boleto - edited.valor_parcelamentos_boleto;
            const consumo_total_gd_s_vigor = (edited.energia_injetada_gd1_kwh + edited.energia_injetada_gd2_kwh) * edited.tarifa_injecao_gd1;
            const valor_adc_bandeira_s_vigor = (edited.energia_injetada_gd1_kwh + edited.energia_injetada_gd2_kwh) * edited.tarifa_bandeira;
            const custo_s_vigor = consumo_total_gd_s_vigor + valor_adc_bandeira_s_vigor;
            const economia = custo_s_vigor - totalComVigor;

            const { faturaId } = await emitirCobrancaAvulsa({
                // Espaço/quebra nas pontas do código vinha daqui (57 de 58 casos, #6477).
                ucCodigo: edited.uc_codigo.trim(),
                nomeQuem: profile?.full_name || user?.email || 'Sistema',
                campos: {
                    total_com_vigor: totalComVigor,
                    economia: economia,
                    custo_s_vigor: custo_s_vigor,
                    consumo_total_gd_s_vigor: consumo_total_gd_s_vigor,
                    valor_adc_bandeira_s_vigor: valor_adc_bandeira_s_vigor,
                    uc_codigo: edited.uc_codigo.trim(),
                    cliente_nome: edited.nome_cliente_boleto,
                    cliente_cpf_cnpj: edited.cpf_cnpj_cliente_boleto,
                    nome_cliente_boleto: edited.nome_cliente_boleto,
                    cpf_cnpj_cliente_boleto: edited.cpf_cnpj_cliente_boleto,
                    end_logradouro: edited.end_logradouro,
                    end_numero: edited.end_numero,
                    end_bairro: edited.end_bairro,
                    end_cep: edited.end_cep,
                    end_cidade: edited.end_cidade,
                    end_uf: edited.end_uf,
                    // Canônico ('JAN/2027'), não o texto digitado: a tela de Cobranças filtra
                    // por igualdade exata, então "0127" ou "ABRILÁJULHO2026" some do mês.
                    mes_referencia: mesNorm,
                    valor_boleto_vigor: edited.valor_boleto_vigor,
                    vencimento_boleto: edited.vencimento_boleto,
                    data_vencimento: edited.vencimento_boleto,
                    data_limite_pagamento_boleto: edited.data_limite_pagamento_boleto || null,
                    valor_consumo_nao_comp: edited.valor_consumo_nao_comp,
                    valor_energia_nao_isenta_boleto: edited.valor_energia_nao_isenta_boleto,
                    valor_restituicao_imposto_boleto: edited.valor_restituicao_imposto_boleto,
                    outros_multas_boleto: edited.outros_multas_boleto,
                    outros_multas_descricao: edited.outros_multas_descricao,
                    valor_parcelamentos_boleto: edited.valor_parcelamentos_boleto,
                    valor_juros_boleto: edited.valor_juros_boleto,
                    parcelamento_descricao: edited.parcelamento_descricao,
                    saldo_remanescente: saldoRemanescente,
                    energia_injetada_gd1_kwh: edited.energia_injetada_gd1_kwh,
                    tarifa_injecao_gd1: edited.tarifa_injecao_gd1,
                    consumo_ativo_kwh: edited.consumo_ativo_kwh,
                    tarifa_bandeira: edited.tarifa_bandeira,
                    energia_injetada_gd2_kwh: edited.energia_injetada_gd2_kwh,
                    tarifa_injecao_gd2: edited.tarifa_injecao_gd2,
                    tarifa_parc_injet_s_desc_gd2: edited.tarifa_parc_injet_s_desc_gd2,
                    qtd_injecao_scee_boleto: edited.qtd_injecao_scee_boleto,
                    qtd_adc_bandeira_boleto: edited.qtd_adc_bandeira_boleto,
                    qtd_parc_injet_boleto: edited.qtd_parc_injet_boleto,
                },
            });

            await logAction(user?.id || 'SYSTEM', 'EMIT_BOLETO_AVULSO', { fatura_id: faturaId, uc: edited.uc_codigo, ressalva_pagador: ressalvaAceitaRef.current });
            await gravarRessalvaPagador(faturaId, ressalvaAceitaRef.current);
            logUsageEvent({ userId: user?.id, modulo: 'financeiro', funcionalidade: 'boletos', acao: 'emitir_boleto_avulso', origem: 'acao' });

            addToast('Cobrança avulsa gerada com sucesso!', 'success');
            if (onSuccess) onSuccess();
            onClose();

        } catch (error: any) {
            console.error('Error emitting avulso:', error);
            setEmissionError(error.message || 'Erro inesperado.');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (field: string, value: string | number) => {
        if (field === 'data_limite_pagamento_boleto') {
            // Usuário definiu manualmente a data limite: para de acompanhar o vencimento.
            setLimiteEditadoManualmente(true);
            setEdited(prev => ({ ...prev, data_limite_pagamento_boleto: value as string }));
            return;
        }
        if (field === 'vencimento_boleto') {
            const novoVenc = value as string;
            setEdited(prev => ({
                ...prev,
                vencimento_boleto: novoVenc,
                // Enquanto não editada manualmente, a data limite acompanha vencimento + 30.
                data_limite_pagamento_boleto: limiteEditadoManualmente
                    ? prev.data_limite_pagamento_boleto
                    : addDaysISO(novoVenc, 30),
            }));
            return;
        }
        setEdited(prev => ({ ...prev, [field]: value }));
        if (field === 'uc_codigo') {
            setUcId(null);
            setUcRegistered(false);
        }
    };

    if (!isOpen) return null;

    const formatBRL = (val: number) => val.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    const linha1_qtd = edited.qtd_injecao_scee_boleto;
    const linha1_tarifa = edited.tarifa_injecao_gd1;
    const linha2_qtd = edited.qtd_adc_bandeira_boleto;
    const linha2_tarifa = edited.tarifa_bandeira;
    const linha5_qtd = edited.qtd_parc_injet_boleto;
    const linha5_tarifa = edited.tarifa_parc_injet_s_desc_gd2;
    const linha6_outros = edited.valor_juros_boleto + edited.outros_multas_boleto;

    return createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in p-2 md:p-4" {...propsFundoModal(() => onClose())}>
            <div className="bg-white dark:bg-gray-800/95 rounded-2xl shadow-2xl w-full max-w-[1400px] h-[95vh] overflow-hidden flex flex-col border dark:border-gray-700" onClick={e => e.stopPropagation()}>

                {/* Header */}
                <div className="p-6 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center bg-gray-50/50 dark:bg-gray-800/40 shrink-0">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
                            <Ticket className="text-teal-500" />
                            Gerar Cobrança Avulsa
                        </h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 ml-9">
                            Preencha os dados do cliente e os valores para emissão imediata.
                        </p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors">
                        <X size={24} className="text-gray-400" />
                    </button>
                </div>

                {emissionError && (
                    <div className="mx-6 mt-6 bg-red-50 dark:bg-red-900/10 border-l-4 border-red-500 p-4 rounded-r-lg shadow-sm">
                        <div className="flex gap-3">
                            <AlertTriangle className="text-red-500 shrink-0" size={20} />
                            <div>
                                <h3 className="font-bold text-red-800 dark:text-red-400 text-sm">Atenção: A emissão falhou!</h3>
                                <p className="text-red-700 dark:text-red-300 text-sm mt-1">{emissionError}</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Content Scroll Area */}
                <div className="p-6 md:p-8 flex-1 overflow-y-auto">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                        
                        {/* Left Column: Client Data */}
                        <div className="lg:col-span-4 space-y-6">
                            <div className="bg-gray-50/50 dark:bg-gray-800/30 p-6 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-inner">
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="p-2 bg-primary/10 rounded-lg text-primary">
                                        <Search size={20} />
                                    </div>
                                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">Buscar Cliente</h3>
                                </div>
                                
                                <div className="space-y-4">
                                    <div className="relative">
                                        <label className="block text-xs font-semibold text-gray-500 mb-1">UC Código *</label>
                                        <div className="relative">
                                            <input 
                                                type="text" 
                                                value={edited.uc_codigo} 
                                                onChange={e => handleChange('uc_codigo', e.target.value)}
                                                onBlur={() => searchUC(edited.uc_codigo)}
                                                className="w-full text-sm bg-white border border-gray-200 rounded-lg px-3 py-2 dark:bg-gray-900 dark:border-gray-700 dark:text-white focus:ring-2 focus:ring-primary/50 outline-none" 
                                            />
                                            {searchingUC ? (
                                                <div className="absolute right-2 top-2 animation-spin">
                                                    <Search size={16} className="text-primary animate-pulse" />
                                                </div>
                                            ) : (
                                                <button 
                                                    onClick={() => searchUC(edited.uc_codigo)}
                                                    className="absolute right-2 top-2 text-gray-400 hover:text-primary transition-colors"
                                                >
                                                    <Search size={16} />
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-xs font-semibold text-gray-500 mb-1">Nome do Cliente *</label>
                                        <input type="text" value={edited.nome_cliente_boleto} onChange={e => handleChange('nome_cliente_boleto', e.target.value)} className="w-full text-sm bg-white border border-gray-200 rounded-lg px-3 py-2 dark:bg-gray-900 dark:border-gray-700 dark:text-white focus:ring-2 focus:ring-primary/50 outline-none" />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-500 mb-1">CPF/CNPJ *</label>
                                        <input type="text" value={edited.cpf_cnpj_cliente_boleto} onChange={e => handleChange('cpf_cnpj_cliente_boleto', e.target.value)} className={`w-full text-sm bg-white border rounded-lg px-3 py-2 dark:bg-gray-900 dark:text-white focus:ring-2 focus:ring-primary/50 outline-none ${alertaPagador ? 'border-amber-400 dark:border-amber-600' : 'border-gray-200 dark:border-gray-700'}`} />
                                        {alertaPagador && (
                                            <p className="mt-1.5 flex items-start gap-1.5 text-[11px] font-medium text-amber-700 dark:text-amber-400">
                                                <AlertTriangle size={13} className="shrink-0 mt-px" />
                                                <span>{alertaPagador}</span>
                                            </p>
                                        )}
                                    </div>
                                    
                                    <div className="h-px bg-gray-200 dark:bg-gray-700 my-4"></div>
                                    
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="col-span-2">
                                            <label className="block text-xs font-semibold text-gray-500 mb-1">Logradouro *</label>
                                            <input type="text" value={edited.end_logradouro} onChange={e => handleChange('end_logradouro', e.target.value)} className="w-full text-sm bg-white border border-gray-200 rounded-lg px-3 py-2 dark:bg-gray-900 dark:border-gray-700 dark:text-white focus:ring-2 focus:ring-primary/50 outline-none" />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-semibold text-gray-500 mb-1">Número *</label>
                                            <input type="text" value={edited.end_numero} onChange={e => handleChange('end_numero', e.target.value)} className="w-full text-sm bg-white border border-gray-200 rounded-lg px-3 py-2 dark:bg-gray-900 dark:border-gray-700 dark:text-white focus:ring-2 focus:ring-primary/50 outline-none" />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-semibold text-gray-500 mb-1">Bairro *</label>
                                            <input type="text" value={edited.end_bairro} onChange={e => handleChange('end_bairro', e.target.value)} className="w-full text-sm bg-white border border-gray-200 rounded-lg px-3 py-2 dark:bg-gray-900 dark:border-gray-700 dark:text-white focus:ring-2 focus:ring-primary/50 outline-none" />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-semibold text-gray-500 mb-1">CEP *</label>
                                            <input type="text" value={edited.end_cep} onChange={e => handleChange('end_cep', e.target.value)} className="w-full text-sm bg-white border border-gray-200 rounded-lg px-3 py-2 dark:bg-gray-900 dark:border-gray-700 dark:text-white focus:ring-2 focus:ring-primary/50 outline-none" />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-semibold text-gray-500 mb-1">UF *</label>
                                            <input type="text" value={edited.end_uf} onChange={e => handleChange('end_uf', e.target.value)} className="w-full text-sm bg-white border border-gray-200 rounded-lg px-3 py-2 dark:bg-gray-900 dark:border-gray-700 dark:text-white focus:ring-2 focus:ring-primary/50 outline-none" />
                                        </div>
                                        <div className="col-span-2">
                                            <label className="block text-xs font-semibold text-gray-500 mb-1">Cidade *</label>
                                            <input type="text" value={edited.end_cidade} onChange={e => handleChange('end_cidade', e.target.value)} className="w-full text-sm bg-white border border-gray-200 rounded-lg px-3 py-2 dark:bg-gray-900 dark:border-gray-700 dark:text-white focus:ring-2 focus:ring-primary/50 outline-none" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Right Column: Billing Table and General Info */}
                        <div className="lg:col-span-8 space-y-6">
                            
                            {/* General Values */}
                            <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl border border-gray-100 dark:border-gray-700 flex flex-wrap gap-6 items-center shadow-sm">
                                <div className="flex-1 min-w-[150px]">
                                    <label className="block text-xs font-bold text-gray-500 mb-1 uppercase tracking-wider">Mês Ref. *</label>
                                    <input type="text" placeholder="MM/AAAA" value={edited.mes_referencia} onChange={e => handleChange('mes_referencia', e.target.value)} className="w-full text-sm bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 dark:bg-gray-900 dark:border-gray-700 dark:text-white focus:ring-2 focus:ring-primary/50 outline-none" />
                                    {/* Texto livre que a normalização não reconhece ("ABRILÁJULHO2026",
                                        "SET/2026 - SALDO REMANESCENTE") quebra o filtro de mês: não emite. */}
                                    {edited.mes_referencia && !isMesReferenciaCanonico(edited.mes_referencia) ? (
                                        <p className="mt-1 text-xs text-red-600 dark:text-red-400 flex items-start gap-1">
                                            <AlertTriangle size={13} className="shrink-0 mt-0.5" />
                                            <span>Só o mês, no formato <strong>MM/AAAA</strong>. O motivo vai na descrição.</span>
                                        </p>
                                    ) : null}
                                    <label className="mt-2 flex items-center gap-2 text-xs font-semibold text-gray-600 dark:text-gray-300 cursor-pointer select-none">
                                        <input
                                            type="checkbox"
                                            checked={saldoRemanescente}
                                            onChange={e => setSaldoRemanescente(e.target.checked)}
                                            className="rounded border-gray-300 text-primary focus:ring-primary/20"
                                        />
                                        Cobrança de saldo remanescente
                                    </label>
                                </div>
                                <div className="flex-1 min-w-[200px]">
                                    <label className="block text-xs font-bold text-gray-500 mb-1 uppercase tracking-wider">Valor do Boleto Vigor</label>
                                    <div className="relative">
                                        <span className="absolute left-3 top-2 text-blue-600 dark:text-blue-400 font-bold">R$</span>
                                        <input 
                                            type="text" 
                                            value={Number(edited.valor_boleto_vigor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} 
                                            onChange={e => {
                                                const digits = e.target.value.replace(/\D/g, '');
                                                const value = parseFloat(digits) / 100;
                                                handleChange('valor_boleto_vigor', isNaN(value) ? 0 : value);
                                            }} 
                                            className="w-full pl-10 pr-3 py-2 text-sm bg-blue-50/50 border border-blue-200 rounded-lg dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-300 font-bold focus:ring-2 focus:ring-blue-500/50 outline-none" 
                                        />
                                    </div>
                                </div>
                                <div className="flex-1 min-w-[180px]">
                                    <label className="block text-xs font-bold text-gray-500 mb-1 uppercase tracking-wider">Vencimento *</label>
                                    <input type="date" value={edited.vencimento_boleto} onChange={e => handleChange('vencimento_boleto', e.target.value)} className="w-full text-sm bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 dark:bg-gray-900 dark:border-gray-700 dark:text-white focus:ring-1 focus:ring-primary/50 outline-none" />
                                </div>
                                <div className="flex-1 min-w-[180px]">
                                    <label className="block text-xs font-bold text-gray-500 mb-1 uppercase tracking-wider">Data Limite de Pagamento</label>
                                    <input type="date" min={edited.vencimento_boleto || undefined} value={edited.data_limite_pagamento_boleto} onChange={e => handleChange('data_limite_pagamento_boleto', e.target.value)} className="w-full text-sm bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 dark:bg-gray-900 dark:border-gray-700 dark:text-white focus:ring-1 focus:ring-primary/50 outline-none" />
                                    <p className="mt-1 text-[11px] leading-tight text-gray-400 dark:text-gray-500">Até quando o boleto pode ser pago. Padrão: vencimento + 30 dias.</p>
                                </div>
                            </div>

                            {/* DETALHES DA COBRANÇA */}
                            <div className="bg-white dark:bg-gray-800 rounded-xl border border-[#485B33] dark:border-[#5C7441] overflow-hidden shadow-sm">
                                <div className="bg-[#485B33] px-4 py-2">
                                    <h4 className="text-white font-bold text-sm tracking-widest uppercase">DETALHES DA COBRANÇA - VIGOR ENERGY</h4>
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm text-left">
                                        <thead className="text-[10px] text-[#485B33] bg-[#E8EAE6] dark:bg-[#202717] dark:text-[#A7B994] font-bold uppercase">
                                            <tr>
                                                <th className="px-4 py-2 w-1/2">DESCRIÇÃO</th>
                                                <th className="px-4 py-2 text-right">QUANTIDADE</th>
                                                <th className="px-4 py-2 text-right">TARIFA</th>
                                                <th className="px-4 py-2 text-right">VALOR (R$)</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-100 dark:divide-gray-800 text-gray-700 dark:text-gray-300">
                                            {/* LINHA 1 */}
                                            <tr className="bg-[#F8F9F7] dark:bg-gray-900/50">
                                                <td className="px-4 py-3">
                                                    <div className="font-bold text-gray-900 dark:text-white">Injeção SCEE - Energia</div>
                                                    <div className="text-xs text-gray-400">Com percentual de desconto</div>
                                                </td>
                                                <td className="px-4 py-2 text-right">
                                                    <input
                                                        type="number" step="0.01"
                                                        value={edited.qtd_injecao_scee_boleto}
                                                        onChange={e => handleChange('qtd_injecao_scee_boleto', Number(e.target.value))}
                                                        className="w-24 text-right bg-gray-50 border border-gray-200 rounded px-2 py-1 text-sm focus:ring-1 focus:ring-primary/50 dark:bg-gray-900 dark:border-gray-700"
                                                    />
                                                </td>
                                                <td className="px-4 py-2 text-right">
                                                    <input
                                                        type="number" step="0.000001"
                                                        value={edited.tarifa_injecao_gd1}
                                                        onChange={e => handleChange('tarifa_injecao_gd1', Number(e.target.value))}
                                                        className="w-24 text-right bg-gray-50 border border-gray-200 rounded px-2 py-1 text-sm focus:ring-1 focus:ring-primary/50 dark:bg-gray-900 dark:border-gray-700"
                                                    />
                                                </td>
                                                <td className="px-4 py-2 text-right font-medium">
                                                    {formatBRL(linha1_qtd * linha1_tarifa)}
                                                </td>
                                            </tr>

                                            {/* LINHA 2 */}
                                            <tr className="bg-white dark:bg-gray-800">
                                                <td className="px-4 py-3">
                                                    <div className="font-bold text-gray-900 dark:text-white">Adicional de bandeira</div>
                                                    <div className="text-xs text-gray-400">Com percentual de desconto</div>
                                                </td>
                                                <td className="px-4 py-2 text-right">
                                                    <input
                                                        type="number" step="0.01"
                                                        value={edited.qtd_adc_bandeira_boleto}
                                                        onChange={e => handleChange('qtd_adc_bandeira_boleto', Number(e.target.value))}
                                                        className="w-24 text-right bg-gray-50 border border-gray-200 rounded px-2 py-1 text-sm focus:ring-1 focus:ring-primary/50 dark:bg-gray-900 dark:border-gray-700"
                                                    />
                                                </td>
                                                <td className="px-4 py-2 text-right">
                                                    <input
                                                        type="number" step="0.000001"
                                                        value={edited.tarifa_bandeira}
                                                        onChange={e => handleChange('tarifa_bandeira', Number(e.target.value))}
                                                        className="w-24 text-right bg-gray-50 border border-gray-200 rounded px-2 py-1 text-sm focus:ring-1 focus:ring-primary/50 dark:bg-gray-900 dark:border-gray-700"
                                                    />
                                                </td>
                                                <td className="px-4 py-2 text-right font-medium">
                                                    {formatBRL(linha2_qtd * linha2_tarifa)}
                                                </td>
                                            </tr>

                                            {/* LINHA 3 */}
                                            <tr className="bg-[#F8F9F7] dark:bg-gray-900/50">
                                                <td className="px-4 py-3 font-bold text-gray-900 dark:text-white">Parc. injet. s/ desconto</td>
                                                <td className="px-4 py-2 text-right">
                                                    <input
                                                        type="number" step="0.01"
                                                        value={edited.qtd_parc_injet_boleto}
                                                        onChange={e => handleChange('qtd_parc_injet_boleto', Number(e.target.value))}
                                                        className="w-24 text-right bg-gray-50 border border-gray-200 rounded px-2 py-1 text-sm focus:ring-1 focus:ring-primary/50 dark:bg-gray-900 dark:border-gray-700"
                                                    />
                                                </td>
                                                <td className="px-4 py-2 text-right">
                                                    <input
                                                        type="number" step="0.000001"
                                                        value={edited.tarifa_parc_injet_s_desc_gd2}
                                                        onChange={e => handleChange('tarifa_parc_injet_s_desc_gd2', Number(e.target.value))}
                                                        className="w-24 text-right bg-gray-50 border border-gray-200 rounded px-2 py-1 text-sm focus:ring-1 focus:ring-primary/50 dark:bg-gray-900 dark:border-gray-700"
                                                    />
                                                </td>
                                                <td className="px-4 py-2 text-right font-medium">
                                                    {formatBRL(-(linha5_qtd * linha5_tarifa))}
                                                </td>
                                            </tr>

                                            {/* LINHA 4 */}
                                            <tr className="bg-white dark:bg-gray-800">
                                                <td className="px-4 py-3 font-bold text-gray-900 dark:text-white">Restituição de Imposto</td>
                                                <td className="px-4 py-2 text-right"></td>
                                                <td className="px-4 py-2 text-right"></td>
                                                <td className="px-4 py-2 text-right">
                                                    <input
                                                        type="number" step="0.01"
                                                        value={edited.valor_restituicao_imposto_boleto}
                                                        onChange={e => handleChange('valor_restituicao_imposto_boleto', Number(e.target.value))}
                                                        className="w-24 text-right bg-gray-50 border border-gray-200 rounded px-2 py-1 text-sm focus:ring-1 focus:ring-primary/50 dark:bg-gray-900 dark:border-gray-700"
                                                    />
                                                </td>
                                            </tr>

                                            {/* LINHA 5 */}
                                            <tr className="bg-[#F8F9F7] dark:bg-gray-900/50">
                                                <td className="px-4 py-3 font-bold text-gray-900 dark:text-white">Energia compensada não isenta (TRIBUTOS) - UC</td>
                                                <td className="px-4 py-2 text-right"></td>
                                                <td className="px-4 py-2 text-right"></td>
                                                <td className="px-4 py-2 text-right">
                                                    <input
                                                        type="number" step="0.01"
                                                        value={edited.valor_energia_nao_isenta_boleto}
                                                        onChange={e => handleChange('valor_energia_nao_isenta_boleto', Number(e.target.value))}
                                                        className="w-24 text-right bg-gray-50 border border-gray-200 rounded px-2 py-1 text-sm focus:ring-1 focus:ring-primary/50 dark:bg-gray-900 dark:border-gray-700"
                                                    />
                                                </td>
                                            </tr>

                                            {/* LINHA 6 */}
                                            <tr className="bg-white dark:bg-gray-800">
                                                <td className="px-4 py-3 align-top">
                                                    <div className="font-bold text-gray-900 dark:text-white mb-2">Multas / Juros / Outros</div>
                                                    <div className="flex items-center gap-2">
                                                        <div className="flex flex-col">
                                                            <span className="text-[10px] text-gray-500 font-semibold mb-0.5 uppercase">Juros</span>
                                                            <input
                                                                type="number" step="0.01"
                                                                value={edited.valor_juros_boleto}
                                                                onChange={e => handleChange('valor_juros_boleto', Number(e.target.value))}
                                                                className="w-20 text-right bg-gray-50 border border-gray-200 rounded px-1 py-1 text-xs focus:ring-1 focus:ring-primary/50 dark:bg-gray-900 dark:border-gray-700"
                                                            />
                                                        </div>
                                                        <span className="text-gray-300 mt-4">+</span>
                                                        <div className="flex flex-col">
                                                            <span className="text-[10px] text-gray-500 font-semibold mb-0.5 uppercase">Multas/Outros</span>
                                                            <input
                                                                type="number" step="0.01"
                                                                value={edited.outros_multas_boleto}
                                                                onChange={e => handleChange('outros_multas_boleto', Number(e.target.value))}
                                                                className="w-20 text-right bg-gray-50 border border-gray-200 rounded px-1 py-1 text-xs focus:ring-1 focus:ring-primary/50 dark:bg-gray-900 dark:border-gray-700"
                                                            />
                                                        </div>
                                                        <div className="flex flex-col flex-1 pl-2 border-l border-gray-200 dark:border-gray-700">
                                                            <span className="text-[10px] text-gray-500 font-semibold mb-0.5 uppercase">Descrição</span>
                                                            <input
                                                                type="text"
                                                                value={edited.outros_multas_descricao}
                                                                onChange={e => handleChange('outros_multas_descricao', e.target.value)}
                                                                placeholder="Ex: Multa por atraso..."
                                                                className="w-full bg-gray-50 border border-gray-200 rounded px-2 py-1 text-xs focus:ring-1 focus:ring-primary/50 dark:bg-gray-900 dark:border-gray-700"
                                                            />
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-2 text-right"></td>
                                                <td className="px-4 py-2 text-right"></td>
                                                <td className="px-4 py-2 text-right font-bold text-primary align-bottom">
                                                    {formatBRL(linha6_outros)}
                                                </td>
                                            </tr>

                                            {/* LINHA 7 */}
                                            <tr className="bg-[#F8F9F7] dark:bg-gray-900/50">
                                                <td className="px-4 py-3 align-top">
                                                    <div className="font-bold text-gray-900 dark:text-white mb-2">Parcelamentos</div>
                                                    <div className="flex items-center gap-2">
                                                        <div className="flex flex-col flex-1">
                                                            <span className="text-[10px] text-gray-500 font-semibold mb-0.5 uppercase">Descrição</span>
                                                            <input
                                                                type="text"
                                                                value={edited.parcelamento_descricao}
                                                                onChange={e => handleChange('parcelamento_descricao', e.target.value)}
                                                                placeholder="Ex: Parcelamento referente a..."
                                                                className="w-full bg-gray-50 border border-gray-200 rounded px-2 py-1 text-xs focus:ring-1 focus:ring-primary/50 dark:bg-gray-900 dark:border-gray-700"
                                                            />
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-2 text-right"></td>
                                                <td className="px-4 py-2 text-right"></td>
                                                <td className="px-4 py-2 text-right align-bottom">
                                                    <input
                                                        type="number" step="0.01"
                                                        value={edited.valor_parcelamentos_boleto}
                                                        onChange={e => handleChange('valor_parcelamentos_boleto', Number(e.target.value))}
                                                        className="w-24 text-right bg-gray-50 border border-gray-200 rounded px-2 py-1 text-sm focus:ring-1 focus:ring-primary/50 dark:bg-gray-900 dark:border-gray-700"
                                                    />
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="p-4 md:p-6 bg-gray-50 dark:bg-gray-800/60 border-t border-gray-100 dark:border-gray-700 flex justify-end items-center gap-4 shrink-0">
                    <button
                        onClick={onClose}
                        disabled={loading}
                        className="px-6 py-2.5 rounded-xl font-bold text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                        Cancelar
                    </button>

                    <button
                        onClick={() => setShowEmitConfirm(true)}
                        disabled={loading || !edited.uc_codigo || !edited.nome_cliente_boleto || edited.valor_boleto_vigor <= 0}
                        className="bg-teal-600 text-white px-8 py-2.5 rounded-xl font-bold shadow-lg shadow-teal-500/30 hover:shadow-teal-500/40 transition-all flex items-center gap-2 disabled:opacity-50"
                    >
                        {loading ? 'Processando...' : (
                            <>
                                <Ticket size={18} /> Gerar Cobrança
                            </>
                        )}
                    </button>
                </div>
            </div>

            <ConfirmationModal
                isOpen={!!pendingConflito}
                onClose={() => setPendingConflito(null)}
                onConfirm={() => {
                    // Pessoas diferentes no mesmo CPF/CNPJ: só admin libera (#6155). Para os demais
                    // o botão só fecha — o caminho é corrigir o cadastro.
                    if (faltaRessalva) return;
                    ressalvaAceitaRef.current = pendingConflito?.pessoaDiferente ? ressalvaPagador.trim() : null;
                    conflitoLiberadoRef.current = true;
                    setPendingConflito(null);
                    handleEmitir();
                }}
                title={pendingConflito?.pessoaDiferente ? "Atenção: este CPF/CNPJ parece de outra pessoa" : "ATENÇÃO: CPF/CNPJ já usado com outro nome"}
                description={
                    <ConflitoPagadorAviso
                        conflito={pendingConflito}
                        nomeEmitido={edited.nome_cliente_boleto}
                        ressalva={ressalvaPagador}
                        onRessalvaChange={setRessalvaPagador}
                        onUsarNomeOficial={(nome) => {
                            // Fecha o aviso: o próximo "Emitir" confere de novo, já com o nome oficial.
                            setEdited(prev => ({ ...prev, nome_cliente_boleto: nome }));
                            setPendingConflito(null);
                        }}
                    />
                }
                confirmText={pendingConflito?.pessoaDiferente ? "Emitir mesmo assim" : "É o mesmo cliente, emitir"}
                confirmDisabled={faltaRessalva}
                cancelText="Cancelar e conferir o cadastro"
                variant={pendingConflito?.pessoaDiferente ? "danger" : "warning"}
            />

            <ConfirmationModal
                isOpen={showEmitConfirm}
                onClose={() => setShowEmitConfirm(false)}
                onConfirm={() => {
                    setShowEmitConfirm(false);
                    handleEmitir();
                }}
                title="Confirmar Emissão Manual"
                description={
                    <div className="text-left mt-3 bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700 font-poppins">
                        <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">Confirme os dados da cobrança:</p>
                        <div className="space-y-1 text-sm">
                            <p><strong>Cliente:</strong> {edited.nome_cliente_boleto}</p>
                            <p><strong>UC:</strong> {edited.uc_codigo}</p>
                            <p><strong>Mês Ref:</strong> {edited.mes_referencia}</p>
                            {saldoRemanescente ? <p><strong>Tipo:</strong> Saldo remanescente</p> : null}
                            <p className="text-lg text-blue-600 dark:text-blue-400 mt-2 font-bold">Valor: R$ {formatBRL(edited.valor_boleto_vigor)}</p>
                        </div>
                        {!ucRegistered && (
                            <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-950/20 border border-yellow-200 dark:border-yellow-900/50 rounded-lg text-xs text-yellow-800 dark:text-yellow-400 flex items-start gap-2">
                                <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                                <div>
                                    <strong className="font-bold">Atenção:</strong> Esta UC não está cadastrada no banco de dados.
                                    <p className="mt-1">Ao confirmar, o sistema solicitará permissão para registrar esta UC.</p>
                                </div>
                            </div>
                        )}
                    </div>
                }
                confirmText="Sim, emitir agora"
                cancelText="Revisar"
                variant="info"
            />

            {/* UC Confirmation Modal */}
            <ConfirmationModal
                isOpen={showUcConfirmModal}
                onClose={() => setShowUcConfirmModal(false)}
                onConfirm={() => {
                    setShowUcConfirmModal(false);
                    setShouldSaveUc(true);
                    executeEmitir(null);
                }}
                title="ATENÇÃO: UC Não Encontrada"
                description={
                    <div className="text-left">
                        <p className="mb-2">
                            A Unidade Consumidora (UC) <strong>{edited.uc_codigo}</strong> não existe no banco de dados.
                        </p>
                        <p className="mb-2">
                            Se este número estiver incorreto, a cobrança não será identificada como duplicada no futuro.
                        </p>
                        <p>
                            Você tem certeza de que o número está correto e deseja registrar esta UC?
                        </p>
                    </div>
                }
                confirmText="Sim, criar nova UC"
                cancelText="Não, vou corrigir o número"
                variant="warning"
            />
        </div>,
        document.body
    );
};

export default BoletoAvulsoModal;
